import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@backoffice/providers';
import {
  ButtonIcon,
  Label,
  FlexContainer,
  PositionX,
  useToast,
  useTimezone,
} from '@facephi/ui-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  ReportNotificationCount,
  ReportNotificationItem,
  ReportNotificationMenu,
  ReportNotificationTrigger,
} from './Styles';

type Notification = {
  id: string;
  reportId: string;
  type: 'ReportReady' | 'ReportFailed';
  occurredAt: string;
  readAt?: string;
  status: string;
  messageKey: string;
};
const apiUrl: string =
  (window as any)._env_.REACT_APP_API_URL?.replace(/\/$/, '') || '';
const sessionSeen = new Set<string>();
let seenScope = '';

export const ReportNotifications = () => {
  const { token, tenant, user, handleRefreshToken } = useAuth();
  const { t } = useTranslation();
  const { toastManager } = useToast();
  const { formatTimezone } = useTimezone();
  const navigate = useNavigate();
  const [items, setItems] = useState<Notification[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const scope = `${tenant.id}:${user?.id || ''}`;
  const scopeRef = useRef(scope);
  scopeRef.current = scope;
  const refreshRef = useRef(handleRefreshToken);
  const toastRef = useRef(toastManager);
  refreshRef.current = handleRefreshToken;
  toastRef.current = toastManager;

  useEffect(() => {
    if (seenScope !== scope) {
      sessionSeen.clear();
      seenScope = scope;
    }
    setItems([]);
    setError(null);
    setOpen(false);
    if (!token || !tenant.id) {
      sessionSeen.clear();
      seenScope = '';
      return;
    }
    const controller = new AbortController();
    let retry = 1000;
    let timer: number | undefined;
    let lastEventId: string | null = null;
    const headers = (accessToken: string) => ({
      Authorization: `Bearer ${accessToken}`,
      'x-inphinite-tenantid': tenant.id,
    });
    const load = async (accessToken: string) => {
      const all: Notification[] = [];
      let cursor: string | null = null;
      do {
        const url = `${apiUrl}/reports/operations/notifications?unreadOnly=true&limit=100${
          cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''
        }`;
        const response = await fetch(url, {
          headers: headers(accessToken),
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const page: { items: Notification[]; nextCursor?: string } =
          await response.json();
        all.push(...page.items);
        page.items.forEach((item) => sessionSeen.add(item.id));
        cursor = page.nextCursor || null;
      } while (cursor && !controller.signal.aborted);
      if (!controller.signal.aborted) setItems(all);
    };
    const receive = (event: Notification) => {
      if (controller.signal.aborted || scopeRef.current !== scope) return;
      if (sessionSeen.has(event.id)) return;
      sessionSeen.add(event.id);
      setItems((current) => [
        event,
        ...current.filter((item) => item.id !== event.id),
      ]);
      toastRef.current({
        type: event.type === 'ReportReady' ? 'success' : 'error',
        message: t(event.messageKey),
      });
    };
    const connect = async () => {
      try {
        const currentToken = (await refreshRef.current()) || token;
        if (!lastEventId) {
          const anchorResponse = await fetch(
            `${apiUrl}/reports/operations/notifications?limit=1`,
            {
              headers: headers(currentToken),
              signal: controller.signal,
            },
          );
          if (!anchorResponse.ok)
            throw new Error(`HTTP ${anchorResponse.status}`);
          const anchor: { items: Notification[] } = await anchorResponse.json();
          lastEventId = anchor.items[0]?.id || null;
        }
        await load(currentToken);
        if (controller.signal.aborted) return;
        const response = await fetch(
          `${apiUrl}/reports/operations/events${
            lastEventId ? `?after=${encodeURIComponent(lastEventId)}` : ''
          }`,
          {
            headers: headers(currentToken),
            signal: controller.signal,
          },
        );
        if (response.status === 400) lastEventId = null;
        if (!response.ok || !response.body)
          throw new Error(`HTTP ${response.status}`);
        setError(null);
        retry = 1000;
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let pending = '';
        while (!controller.signal.aborted) {
          const { value, done } = await reader.read();
          if (done) break;
          pending += decoder.decode(value, { stream: true });
          const frames = pending.split(/\r?\n\r?\n/);
          pending = frames.pop() || '';
          for (const frame of frames) {
            const eventId = frame
              .split(/\r?\n/)
              .find((line) => line.startsWith('id: '));
            if (eventId) lastEventId = eventId.slice(4);
            const data = frame
              .split(/\r?\n/)
              .find((line) => line.startsWith('data: '));
            if (data) receive(JSON.parse(data.slice(6)));
          }
        }
      } catch (failure: any) {
        if (!controller.signal.aborted)
          setError(failure.message || t('Notifications unavailable'));
      }
      if (!controller.signal.aborted) {
        const wait = retry + Math.floor((Math.random() * retry) / 4);
        retry = Math.min(retry * 2, 30000);
        timer = window.setTimeout(connect, wait);
      }
    };
    connect();
    return () => {
      controller.abort();
      if (timer) window.clearTimeout(timer);
    };
  }, [token, tenant.id, user?.id, t]);

  const openReport = async (item: Notification) => {
    if (!token) return;
    const requestedScope = scope;
    try {
      const response = await fetch(
        `${apiUrl}/reports/operations/notifications/${item.id}/read`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'x-inphinite-tenantid': tenant.id,
          },
        },
      );
      if (scopeRef.current !== requestedScope) return;
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      setItems((current) => current.filter((entry) => entry.id !== item.id));
      setOpen(false);
      navigate(`/operation-reports/${item.reportId}`);
    } catch (failure: any) {
      if (scopeRef.current === requestedScope)
        setError(failure.message || t('Notifications unavailable'));
    }
  };

  return (
    <ReportNotificationMenu
      widthAuto
      tooltip={t('Report notifications')}
      positionX={PositionX.end}
      open={open}
      updateOpen={setOpen}
      renderItem={
        <ReportNotificationTrigger>
          <ButtonIcon
            iconName="Bell"
            variant="text"
            testId="report-notifications"
          />
          {items.length > 0 && (
            <ReportNotificationCount aria-live="polite">
              {items.length > 99 ? '99+' : items.length}
            </ReportNotificationCount>
          )}
        </ReportNotificationTrigger>
      }
    >
      <ReportNotificationItem disabled>
        <Label fontSize="14" semibold>
          {t('Report notifications')}
        </Label>
      </ReportNotificationItem>
      {error && (
        <ReportNotificationItem disabled iconName="WarningCircle">
          <Label fontSize="14" color="error400" role="alert">
            {error}
          </Label>
        </ReportNotificationItem>
      )}
      {items.map((item) => (
        <ReportNotificationItem
          key={item.id}
          iconName={
            item.type === 'ReportReady' ? 'CheckCircle' : 'WarningCircle'
          }
          onClick={() => openReport(item)}
        >
          <FlexContainer
            flexDirection="column"
            rowGap="0.4"
            alignItems="flex-start"
          >
            <Label fontSize="14" semibold>
              {t(item.messageKey)}
            </Label>
            <Label fontSize="12" color="grey500">
              {formatTimezone(item.occurredAt)}
            </Label>
          </FlexContainer>
        </ReportNotificationItem>
      ))}
      {!error && items.length === 0 && (
        <ReportNotificationItem disabled>
          <Label fontSize="14" color="grey500">
            {t('No unread notifications')}
          </Label>
        </ReportNotificationItem>
      )}
      <ReportNotificationItem
        iconName="FileText"
        onClick={() => {
          setOpen(false);
          navigate('/operation-reports');
        }}
      >
        {t('All reports')}
      </ReportNotificationItem>
    </ReportNotificationMenu>
  );
};
